import { SPY_REPORT_TIER_ODDS, SPY_SCAN_COOLDOWN_TABLE, SPY_SCAN_DISCOVERY_ODDS, SPY_SCAN_DESTROY_CHANCE_NO_SHIELD, } from "@kaeliux/shared";
/**
 * Espionage resolution: spy-report tier selection and counter-espionage
 * scan (discovery/destruction of loitering spy ships). Pure functions
 * only — randomness (`roll` parameters) and time (`now`) are always
 * injected by the caller (`apps/functions/src/fleets.ts` rolls via
 * `Math.random()` once, outside the Firestore transaction, mirroring
 * `asteroidRoll`/`battleRolls` in `fleetResolution.ts`).
 *
 * Odds tables live in `packages/shared/src/constants/espionage.ts` — see
 * that file's doc comments for the source design and the deliberate
 * deviations from it (the "none" tier, the no-shield-only cooldown/destroy
 * numbers).
 */
function clampRoll(roll) {
    return Math.min(1, Math.max(0, roll));
}
/**
 * Walk an ascending table (by whatever numeric field `minOf` reads), keeping
 * the last row whose bound is `<= value` (mirrors `calculateDemolitionChance`).
 */
function lastMatchingRow(table, value, minOf) {
    // Every table this consumes is a non-empty constant defined in
    // constants/espionage.ts — the non-null assertion just satisfies
    // `noUncheckedIndexedAccess`, not a real runtime possibility.
    let match = table[0];
    for (const row of table) {
        if (value >= minOf(row))
            match = row;
    }
    return match;
}
// ---------------------------------------------------------------------------
// Spy report tier
// ---------------------------------------------------------------------------
/**
 * Picks a spy-report tier from `diff` (spy's Intelligence level − target's
 * Intelligence level) and an injected 0..1 `roll`. Buckets the roll against
 * the row's cumulative none/full/basic bounds, in that order (so `roll = 0`
 * always lands on the best-case outcome for the given diff).
 */
export function pickSpyReportTier(diff, roll) {
    const row = lastMatchingRow(SPY_REPORT_TIER_ODDS, diff, (r) => r.minDiff);
    const clamped = clampRoll(roll);
    const noneBound = row.none;
    const fullBound = noneBound + row.full;
    if (clamped < noneBound)
        return "none";
    if (clamped < fullBound)
        return "full";
    return "basic";
}
/**
 * Builds the tier-gated `SpyReport` fields for a captured planet.
 * `undefined` for tier `"none"` — no report is produced at all. Otherwise
 * `resources`/`energy` are always present, and `ships` (the target's docked
 * count at capture time) is added at `"full"` only.
 *
 * The target's defences belong in the `"full"` branch too, and go in as
 * soon as a defense/turret building exists to read them from — see
 * `SpyReport`'s TODO(defences) and `docs/game-details/flight_rules.md`'s
 * "Known stubs". Building levels and population capacity, by contrast, are
 * deliberately absent from every tier.
 */
export function buildSpyReportFields(target, tier) {
    if (tier === "none")
        return undefined;
    const fields = {
        resources: {
            metal: Math.floor(target.resources.metal.current),
            minerals: Math.floor(target.resources.minerals.current),
            gas: Math.floor(target.resources.gas.current),
            scrap: Math.floor(target.resources.scrap.current),
        },
        energy: Math.floor(target.resources.energy.current),
    };
    if (tier === "full") {
        fields.ships = { ...target.ships };
    }
    return fields;
}
// ---------------------------------------------------------------------------
// Counter-espionage scan: cooldown
// ---------------------------------------------------------------------------
/** Cooldown (minutes) before the next scan may run, keyed by the SCANNER's own Intelligence level. */
export function spyScanCooldownMinutes(scannerIntelligenceLevel) {
    return lastMatchingRow(SPY_SCAN_COOLDOWN_TABLE, scannerIntelligenceLevel, (r) => r.minLevel).cooldownMinutes;
}
/** Same cooldown, in seconds — the unit `resolveSpyScan`/the functions layer actually work in. */
export function spyScanCooldownSeconds(scannerIntelligenceLevel) {
    return spyScanCooldownMinutes(scannerIntelligenceLevel) * 60;
}
/** Seconds remaining before the next scan may run — 0 (never negative) once the cooldown has elapsed, or if the planet has never scanned. */
export function spyScanCooldownRemainingSeconds(lastScanAt, scannerIntelligenceLevel, now) {
    if (!lastScanAt)
        return 0;
    const cooldownMs = spyScanCooldownSeconds(scannerIntelligenceLevel) * 1000;
    const elapsedMs = now.getTime() - lastScanAt.getTime();
    return Math.max(0, Math.ceil((cooldownMs - elapsedMs) / 1000));
}
// ---------------------------------------------------------------------------
// Counter-espionage scan: discovery & destroy
// ---------------------------------------------------------------------------
/** Chance to discover a loitering probe, from `diff` (scanner's Intelligence level − probe owner's Intelligence level). */
export function spyScanDiscoveryChance(diff) {
    return lastMatchingRow(SPY_SCAN_DISCOVERY_ODDS, diff, (r) => r.minDiff).discoverChance;
}
/** Whether an injected 0..1 `roll` discovers the probe, given `diff`. */
export function rollSpyScanDiscovery(diff, roll) {
    return clampRoll(roll) < spyScanDiscoveryChance(diff);
}
/** Whether an injected 0..1 `roll` destroys an already-discovered probe (flat no-shield chance). */
export function rollSpyScanDestroy(roll) {
    return clampRoll(roll) < SPY_SCAN_DESTROY_CHANCE_NO_SHIELD;
}
/**
 * Resolves a full counter-espionage scan: cooldown gate first, then rolls
 * discovery (and, if discovered, destroy) for every candidate probe. The
 * functions layer is responsible for finding which probes are currently
 * loitering at the target coordinates (`mission === 'espionage' && status
 * === 'returning' && !recalled && now < returnsAt`) and for actually
 * mutating a destroyed probe's fleet/planet docs — this function only
 * decides the outcome.
 */
export function resolveSpyScan(input) {
    const cooldownRemainingSeconds = spyScanCooldownRemainingSeconds(input.lastScanAt, input.scannerIntelligenceLevel, input.now);
    if (cooldownRemainingSeconds > 0) {
        return { cooldownRemainingSeconds, results: [] };
    }
    const results = [];
    for (const probe of input.probes) {
        const diff = input.scannerIntelligenceLevel - probe.ownerIntelligenceLevel;
        if (!rollSpyScanDiscovery(diff, probe.discoveryRoll))
            continue;
        results.push({
            fleetId: probe.fleetId,
            ownerUserId: probe.ownerUserId,
            destroyed: rollSpyScanDestroy(probe.destroyRoll),
        });
    }
    return { cooldownRemainingSeconds: 0, results };
}
//# sourceMappingURL=espionage.js.map